import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthHeaders } from '@/lib/hooks/use-auth-headers';
import { useUnifiedCalendar } from '@/lib/hooks/use-unified-calendar';
import { apiFetch } from '@/lib/api/apiFetch';
import { addDays, todayString } from '@/lib/utils/dateHelpers';
import type { CalendarSourceType, UnifiedCalendarEvent } from '@/lib/types/calendar';

/** How far back an unfinished item still counts as "overdue" on the home page. */
const OVERDUE_LOOKBACK_DAYS = 30;
/** How far ahead counts as "coming up". */
const LOOKAHEAD_DAYS = 7;

/** Statuses that mean the item is settled — nothing left for the director to do. */
const DONE_STATUSES = new Set(['completed', 'approved', 'resolved', 'closed', 'verified', 'waived']);

/**
 * Sources that are informational rather than actionable. Approved PTO and room
 * transitions are worth knowing but nobody has to act on them, so they belong on
 * the calendar rather than in a "needs you" list.
 */
const INFO_SOURCES = new Set<CalendarSourceType>(['pto', 'transition']);

export type AttentionBucket = 'overdue' | 'today' | 'upcoming';

export interface AttentionItem extends UnifiedCalendarEvent {
  bucket: AttentionBucket;
}

export interface HomeAttention {
  items: AttentionItem[];
  counts: Record<AttentionBucket, number>;
  /** PTO requests waiting on an approver — only fetched for users who can approve. */
  ptoPending: number;
  isLoading: boolean;
  /** False when the caller has no calendar-bearing hub — the feed has nothing to say. */
  enabled: boolean;
}

/**
 * The "needs your attention" feed for the home page: everything dated within the
 * overdue window or the next week that the caller can still act on.
 *
 * Access is enforced server-side — `/api/calendar/unified` only returns events
 * from hubs the caller already has access to — so this hook never has to decide
 * what a user is allowed to see.
 */
export function useHomeAttention(opts: { enabled: boolean; canApprovePto: boolean }): HomeAttention {
  const today = todayString();
  const { headers, activeSchoolId, enabled: authEnabled } = useAuthHeaders();

  const { events, isLoading } = useUnifiedCalendar(
    opts.enabled ? addDays(today, -OVERDUE_LOOKBACK_DAYS) : '',
    opts.enabled ? addDays(today, LOOKAHEAD_DAYS) : '',
  );

  const { data: ptoData } = useQuery({
    queryKey: ['home-pto-pending', activeSchoolId],
    queryFn: () => apiFetch<{ id: string }[]>('/api/documents/pto?status=pending', headers),
    enabled: authEnabled && opts.enabled && opts.canApprovePto,
  });

  const items = useMemo(() => {
    return events
      .filter((e) => !INFO_SOURCES.has(e.source))
      .filter((e) => !DONE_STATUSES.has(e.status ?? ''))
      .map((e): AttentionItem => ({
        ...e,
        bucket: e.date < today ? 'overdue' : e.date === today ? 'today' : 'upcoming',
      }))
      // Overdue first (oldest first), then today, then soonest upcoming.
      .sort((a, b) => {
        const order: Record<AttentionBucket, number> = { overdue: 0, today: 1, upcoming: 2 };
        return order[a.bucket] - order[b.bucket] || a.date.localeCompare(b.date);
      });
  }, [events, today]);

  const counts = useMemo(
    () =>
      items.reduce(
        (acc, i) => ({ ...acc, [i.bucket]: acc[i.bucket] + 1 }),
        { overdue: 0, today: 0, upcoming: 0 } as Record<AttentionBucket, number>,
      ),
    [items],
  );

  return { items, counts, ptoPending: ptoData?.length ?? 0, isLoading, enabled: opts.enabled };
}
